// src/components/Lms/Pages/CoursesMarketplace.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FiBookOpen, FiSearch, FiClock } from "react-icons/fi";
import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { cursosNormalize } from "../Objetos/CursosModel";
import { getSessionUserId } from "../../../utils/SessionUser";
import AlertDialog from "../Components/Shared/AlertDialog";
import ConfirmDialog from "../Components/Shared/ConfirmDialog";

const CoursesMarketplace = () => {
    const navigate = useNavigate();
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [relaciones, setRelaciones] = useState([]); // EstudianteCurso existentes
    const [idEstudiante, setIdEstudiante] = useState(null);
    const [registeringId, setRegisteringId] = useState(null);
    const [niveles, setNiveles] = useState([]); // Catálogo de niveles
    const [tiposHabilidad, setTiposHabilidad] = useState([]); // Catálogo de tipos de habilidad
    const [isAdminLMS, setIsAdminLMS] = useState(false); // Si el usuario es Administrador LMS
    const [isEstudiante, setIsEstudiante] = useState(false); // Si el usuario es Estudiante
    const [peticionesCertificado, setPeticionesCertificado] = useState([]); // Peticiones de certificado
    const [vistaAdmin, setVistaAdmin] = useState('revisar'); // 'revisar' o 'todos' (solo para Admin LMS)
    const [validationsComplete, setValidationsComplete] = useState(false); // Flag para saber si las validaciones iniciales terminaron
    const [revisionCount, setRevisionCount] = useState(0);

    // Filtros visuales
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSkill, setSelectedSkill] = useState("");
    const [selectedLevel, setSelectedLevel] = useState("");
    const [selectedAutoAssign, setSelectedAutoAssign] = useState("");

    // Estados para modales
    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    // 1. Cargar idEstudiante y verificar si es Administrador LMS
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const idUsuario = getSessionUserId();
                if (!idUsuario) {
                    setValidationsComplete(true);
                    return;
                }

                const [resUsuarioRoles, resEstudiante] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.usuarioRolByUsuario(idUsuario)),
                    axios.get(LMS_ENDPOINTS.estudianteByUsuario(idUsuario)).catch(() => ({ data: null })),
                ]);

                const usuarioRoles = resUsuarioRoles.data || [];
                const estudianteRecord = resEstudiante.data ? Models.estudiante(resEstudiante.data) : null;

                const tieneRolAdminLMS = usuarioRoles.some(ur => Number(ur.idRol || ur.IdRol) === 1008);
                const tieneRolEstudiante = usuarioRoles.some(ur => Number(ur.idRol || ur.IdRol) === 3);

                setIsAdminLMS(tieneRolAdminLMS);
                setIsEstudiante(tieneRolEstudiante);

                if (tieneRolAdminLMS && !tieneRolEstudiante) {
                    setVistaAdmin('revisar');
                } else {
                    setVistaAdmin('todos');
                }

                if (estudianteRecord) {
                    setIdEstudiante(estudianteRecord.idEstudiante ?? estudianteRecord.IdEstudiante);
                }

                setValidationsComplete(true);
            } catch (error) {
                console.error('[CoursesMarketplace] Error en validación inicial:', error);
                setValidationsComplete(true);
            }
        };

        loadInitialData();
    }, []);

    // 2. Cargar metadatos (Niveles y Habilidades) una sola vez
    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const [resNiveles, resTiposHabilidad] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.catNivel),
                    axios.get(LMS_ENDPOINTS.catTipoHabilidad),
                ]);
                setNiveles((resNiveles.data || []).map(Models.niveles));
                setTiposHabilidad((resTiposHabilidad.data || []).map(Models.tipoHabilidad));
            } catch (error) {
                console.error('[CoursesMarketplace] Error cargando metadatos:', error);
            }
        };
        loadMetadata();
    }, []);

    // 3. Debounce para la búsqueda (600ms)
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(searchTerm), 600);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // 4. Cargar Cursos y Peticiones (Data principal)
    useEffect(() => {
        if (!validationsComplete) return;

        const loadContent = async () => {
            setLoading(true);
            try {
                const idUsuario = getSessionUserId();
                let respCursos;

                // Cursos según vista
                if (vistaAdmin === 'todos') {
                    const params = {
                        idNivel: selectedLevel || undefined,
                        idTipoHabilidad: selectedSkill || undefined,
                        search: debouncedSearch || undefined
                    };
                    respCursos = await axios.get(LMS_ENDPOINTS.cursosMarketplace(idUsuario, params));
                } else if (vistaAdmin === 'revisar') {
                    respCursos = await axios.get(LMS_ENDPOINTS.cursosPendientesRevision(idUsuario));
                } else {
                    respCursos = await axios.get(LMS_ENDPOINTS.cursos);
                }

                // Peticiones
                if (isAdminLMS) {
                    const resPeticiones = await axios.get(LMS_ENDPOINTS.peticionesCertificado).catch(() => ({ data: [] }));
                    const filteredPetitions = (resPeticiones.data || []).filter(
                        p => Number(p.idUsuarioSolicitado || p.IdUsuarioSolicitado) === Number(idUsuario) &&
                            !(p.atendido || p.Atendido)
                    );
                    setPeticionesCertificado(filteredPetitions);

                    // Siempre cargar el conteo de revisión técnica (independientemente de la vista actual)
                    const resPend = await axios.get(LMS_ENDPOINTS.cursosPendientesRevision(idUsuario)).catch(() => ({ data: [] }));
                    const rawPend = resPend.data || [];
                    
                    // Filtrar: Solo contar cursos que tengan una petición pendiente enviada a ESTE administrador
                    const validRevisionCourses = rawPend.filter(c => {
                        const idC = c.idCurso || c.id || c.IdCurso;
                        return filteredPetitions.some(p => Number(p.idCurso || p.IdCurso) === Number(idC));
                    });
                    
                    setRevisionCount(validRevisionCourses.length);

                    // Si estamos en vista 'revisar', el setCursos debe usar validRevisionCourses
                    if (vistaAdmin === 'revisar') {
                        const enrichedRevision = validRevisionCourses.map(c => {
                            const normalized = cursosNormalize(c);
                            return {
                                ...normalized,
                                nivelNombre: c.nivelNombre || c.NivelNombre || (niveles.find(n => n.idNivel === normalized.idNivel)?.nombre) || 'No especificado',
                                tipoHabilidadNombre: c.tipoHabilidadNombre || c.TipoHabilidadNombre || (tiposHabilidad.find(t => t.idTipoHabilidad === normalized.idTipoHabilidad)?.nombre) || 'General',
                                instructorNombre: c.instructorNombreCompleto || c.InstructorNombreCompleto || normalized.instructorNombre || 'Consultar en revisión'
                            };
                        });
                        setCursos(enrichedRevision);
                        setLoading(false);
                        return; // Salir para no sobreescribir con el map general de abajo
                    }
                }

                // Relaciones (para validación)
                if (idEstudiante) {
                    const resRel = await axios.get(LMS_ENDPOINTS.estudiantesCursoByEstudiante(idEstudiante)).catch(() => ({ data: [] }));
                    setRelaciones((resRel.data || []).map(Models.estudiantesCurso));
                }

                // Enriquecimiento (Backend optimizado)
                const rawCursos = respCursos.data || [];
                const enriched = rawCursos.map(c => {
                    const normalized = cursosNormalize(c);
                    return {
                        ...normalized,
                        nivelNombre: c.nivelNombre || c.NivelNombre || (niveles.find(n => n.idNivel === normalized.idNivel)?.nombre) || 'No especificado',
                        tipoHabilidadNombre: c.tipoHabilidadNombre || c.TipoHabilidadNombre || (tiposHabilidad.find(t => t.idTipoHabilidad === normalized.idTipoHabilidad)?.nombre) || 'General',
                        instructorNombre: c.instructorNombreCompleto || c.InstructorNombreCompleto || normalized.instructorNombre || 'Consultar en revisión'
                    };
                });

                setCursos(enriched);
            } catch (error) {
                console.error("[CoursesMarketplace] Error cargando contenido:", error);
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, [isAdminLMS, vistaAdmin, validationsComplete, selectedLevel, selectedSkill, debouncedSearch, idEstudiante]);

    // handlers
    const handleRegister = async (curso) => {
        if (!idEstudiante) {
            setAlertDialog({
                open: true,
                title: 'Error',
                message: 'No se pudo identificar al estudiante actual. No es posible registrar el curso.',
                variant: 'error'
            });
            return;
        }

        const idCurso = curso.idCurso || curso.id || curso.IdCurso;
        if (!idCurso) {
            setAlertDialog({ open: true, title: 'Error', message: 'ID de curso no válido.', variant: 'error' });
            return;
        }

        const yaInscrito = relaciones.some(r => Number(r.idCurso) === Number(idCurso));
        if (yaInscrito) {
            setAlertDialog({ open: true, title: 'Información', message: 'Ya estás inscrito en este curso.', variant: 'info' });
            setCursos(prev => prev.filter(c => Number(c.idCurso || c.id) !== Number(idCurso)));
            return;
        }

        setConfirmDialog({
            open: true,
            title: 'Confirmar inscripción',
            message: `¿Deseas registrarte al curso "${curso.nombre}"?`,
            onConfirm: () => proceedWithRegistration(curso, idCurso)
        });
    };

    const proceedWithRegistration = async (curso, idCurso) => {
        try {
            setRegisteringId(idCurso);
            const resStored = await axios.get(LMS_ENDPOINTS.estudiantesCursoStored(idEstudiante));
            const existingRecord = (resStored.data || []).find(r => Number(r.idCurso) === Number(idCurso));

            if (existingRecord) {
                if (!existingRecord.activo) {
                    await axios.post(LMS_ENDPOINTS.estudiantesCursoReactivate(existingRecord.idEstudiantesCursos));
                    const normalized = Models.estudiantesCurso({ ...existingRecord, activo: true });
                    setRelaciones(prev => [...prev, normalized]);
                }
            } else {
                const dto = {
                    idEstudiantesCursos: 0,
                    idEstudiante: Number(idEstudiante),
                    idCurso: Number(idCurso),
                    calificacionFinal: 0,
                    idEstado: 1,
                    certificadoGenerado: false,
                    ultimoAcceso: new Date().toISOString(),
                    activo: true,
                };
                const resp = await axios.post(LMS_ENDPOINTS.estudiantesCurso, dto);
                const createdId = typeof resp.data === 'number' ? resp.data : (resp.data?.idEstudiantesCursos || 0);
                const normalized = Models.estudiantesCurso({ ...dto, idEstudiantesCursos: createdId });
                setRelaciones(prev => [...prev, normalized]);
            }

            setCursos(prev => prev.filter(c => Number(c.idCurso || c.id) !== Number(idCurso)));
            setAlertDialog({ open: true, title: '¡Éxito!', message: 'Te has registrado correctamente.', variant: 'success' });
        } catch (error) {
            console.error("Error registration:", error);
            setAlertDialog({ open: true, title: 'Error', message: 'Ocurrió un error al registrar el curso.', variant: 'error' });
        } finally {
            setRegisteringId(null);
        }
    };

    return (
        <div className="panel-sigma-main">
            <div className="profile-header-title">
                <h1>Catálogo de cursos</h1>
            </div>

            {/* UI de Toggle, Búsqueda y Filtros - Estilo "Referencia/Comentario" */}
            <div style={{ marginBottom: '2rem', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))' }}>

                {/* 1. Header (Toggle Tabs u Opcion Única) */}
                <div style={{
                    display: 'flex',
                    width: '100%',
                    backgroundColor: '#f1f5f9',
                    borderRadius: '12px 12px 0 0',
                    overflow: 'hidden',
                    padding: '0.4rem',
                    gap: '0.5rem',
                    border: '1px solid #e2e8f0',
                    borderBottom: 'none'
                }}>
                    {(isAdminLMS && isEstudiante) ? (
                        /* Caso Dual: Toggle completo */
                        <>
                            <div
                                onClick={() => setVistaAdmin('revisar')}
                                style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    padding: '0.4rem 0',
                                    fontSize: '0.9rem',
                                    fontWeight: vistaAdmin === 'revisar' ? 700 : 500,
                                    color: vistaAdmin === 'revisar' ? 'var(--color-primary-dark)' : '#64748b',
                                    backgroundColor: vistaAdmin === 'revisar' ? '#ffffff' : 'transparent',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    userSelect: 'none',
                                    boxShadow: vistaAdmin === 'revisar' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                📋 Revisión ({revisionCount})
                            </div>
                            <div
                                onClick={() => setVistaAdmin('todos')}
                                style={{
                                    flex: 1,
                                    textAlign: 'center',
                                    padding: '0.4rem 0',
                                    fontSize: '0.9rem',
                                    fontWeight: vistaAdmin === 'todos' ? 700 : 500,
                                    color: vistaAdmin === 'todos' ? 'var(--color-primary-dark)' : '#64748b',
                                    backgroundColor: vistaAdmin === 'todos' ? '#ffffff' : 'transparent',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    userSelect: 'none',
                                    boxShadow: vistaAdmin === 'todos' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                }}
                            >
                                📚 Catálogo completo
                            </div>
                        </>
                    ) : (
                        /* Caso Single Role: Tab estático para mantener diseño */
                        <div
                            style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '0.4rem 0',
                                fontSize: '0.9rem',
                                fontWeight: 700,
                                color: 'var(--color-primary-dark)',
                                backgroundColor: '#ffffff',
                                borderRadius: '8px',
                                userSelect: 'none',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                        >
                            {isAdminLMS ? `📋 Revisión (${revisionCount})` : "📚 Catálogo de Cursos"}
                        </div>
                    )}
                </div>

                {/* 2. Body (Search & Filters) */}
                <div style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderTop: 'none',
                    borderRadius: '0 0 12px 12px',
                    padding: '0.8rem 1rem',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    {/* Búsqueda */}
                    <div style={{
                        flex: 1,
                        minWidth: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '999px',
                        padding: '0.4rem 1rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                        <FiSearch style={{ color: 'var(--color-primary-dark)', marginRight: '0.6rem', fontSize: '1rem' }} />
                        <input
                            type="text"
                            className="lms-search-input"
                            placeholder="Buscar cursos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ border: 'none', outline: 'none', background: 'transparent', width: '100%', fontSize: '0.9rem', color: '#1e293b' }}
                        />
                    </div>

                    {/* Filtros */}
                    <div style={{ display: 'flex', gap: '0.8rem', flex: '0 0 auto', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <select
                            value={selectedSkill}
                            onChange={(e) => setSelectedSkill(e.target.value)}
                            style={{
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                padding: '0.4rem 2rem 0.4rem 1rem',
                                fontSize: '0.85rem',
                                color: '#475569',
                                outline: 'none',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                minWidth: '160px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        >
                            <option value="">Habilidad</option>
                            {tiposHabilidad.map(th => (
                                <option key={th.idTipoHabilidad} value={th.idTipoHabilidad}>{th.nombre}</option>
                            ))}
                        </select>

                        <select
                            value={selectedLevel}
                            onChange={(e) => setSelectedLevel(e.target.value)}
                            style={{
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                padding: '0.4rem 2rem 0.4rem 1rem',
                                fontSize: '0.85rem',
                                color: '#475569',
                                outline: 'none',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                minWidth: '140px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        >
                            <option value="">Nivel</option>
                            {niveles.map(n => (
                                <option key={n.idNivel} value={n.idNivel}>{n.nombre}</option>
                            ))}
                        </select>

                        <select
                            value={selectedAutoAssign}
                            onChange={(e) => setSelectedAutoAssign(e.target.value)}
                            style={{
                                borderRadius: '999px',
                                border: '1px solid #e2e8f0',
                                padding: '0.4rem 2rem 0.4rem 1rem',
                                fontSize: '0.85rem',
                                color: '#475569',
                                outline: 'none',
                                backgroundColor: '#fff',
                                cursor: 'pointer',
                                minWidth: '150px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}
                        >
                            <option value="">Tipo Inscripción</option>
                            <option value="true">Libre Inscripción</option>
                            <option value="false">Requiere Asignación</option>
                        </select>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="catalog-loading">Cargando cursos...</div>
            ) : (
                <div className="courses-grid fixed-cards">
                    {(() => {
                        // Filtrar cursos en los que el estudiante ya está inscrito
                        // PERO si es Admin LMS en modo "revisar", mostrar siempre
                        const visibles = (!cursos ? [] : cursos).filter((curso) => {
                            // Excepción para Admin en modo revisar
                            if (isAdminLMS && vistaAdmin === 'revisar') {
                                return true;
                            }

                            // Si usamos el endpoint de 'disponibles', ya vienen filtrados
                            // Solo aplicamos el filtro manual si cargamos TODOS los cursos (LMS_ENDPOINTS.cursos)
                            const idUsuario = getSessionUserId();
                            const cargamosTodos = (vistaAdmin !== 'todos' || !idUsuario);

                            if (cargamosTodos && idEstudiante) {
                                const idCurso = curso.idCurso || curso.id || curso.IdCurso;
                                if (!idCurso) return true;
                                const inscrito = relaciones.some(
                                    (rel) =>
                                        String(rel.idCurso) === String(idCurso) &&
                                        String(rel.idEstudiante) === String(idEstudiante)
                                );
                                return !inscrito;
                            }
                            return true;
                        })
                            // Aplicar filtros visuales (Búsqueda inmediata)
                            .filter(c => {
                                // Búsqueda (filtramos localmente para que sea instantáneo mientras escriben)
                                if (searchTerm && !(c.nombre || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
                                
                                // Si NO estamos en modo revisión, aplicar filtros de completitud
                                if (vistaAdmin !== 'revisar') {
                                    // Solo mostrar cursos terminados para el público
                                    // Eliminamos el check por tieneCertificado ya que no se incluye en el DTO de Marketplace
                                    if (!c.terminado) return false;
                                }

                                // Filtro por autoAsignable
                                if (selectedAutoAssign !== "" && String(c.autoAsignable) !== selectedAutoAssign) return false;
                                return true;
                            });

                        if (visibles.length === 0) {
                            return (
                                <div
                                    style={{
                                        gridColumn: "1 / -1",
                                        textAlign: "center",
                                        padding: "3rem",
                                        color: "#94a3b8",
                                    }}
                                >
                                    No hay cursos disponibles en este momento.
                                </div>
                            );
                        }

                        return visibles.map((curso, index) => {
                            const imagePath =
                                curso.uRLRutaImagen ||
                                curso.urlRutaImagen ||
                                curso.URLRutaImagen ||
                                "";
                            const hasImage = !!imagePath;

                            // Si imagePath ya es una URL completa (http:// o https://), usarla directamente
                            // De lo contrario, concatenar con SERVER_URL
                            const imageUrl = imagePath.startsWith('http://') || imagePath.startsWith('https://')
                                ? imagePath
                                : `${SERVER_URL}${imagePath}`;

                            return (
                                <article key={curso.idCurso || curso.id || index} className="course-card fixed-card">
                                    {hasImage ? (
                                        <div className="course-thumb" style={{ padding: 0 }}>
                                            <img
                                                src={imageUrl}
                                                alt={curso.nombre || "Imagen del curso"}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "fill",
                                                    display: "block",
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            className={`course-thumb course-thumb-${curso.thumb || 1}`}
                                        />
                                    )}
                                    <div className="course-body">
                                        <h3 className="course-title">{curso.nombre}</h3>
                                        <div style={{ marginTop: "0.8rem" }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.6rem' }}>
                                                {/* Habilidad - Barra completa */}
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    backgroundColor: '#e0f2fe',
                                                    color: '#0369a1',
                                                    padding: '4px 12px',
                                                    borderRadius: '9999px',
                                                    fontWeight: 600,
                                                    width: '100%',
                                                    display: 'block',
                                                    textAlign: 'left'
                                                }}>
                                                    {curso.tipoHabilidadNombre || "General"}
                                                </div>

                                                {/* Nivel - Barra completa */}
                                                <div style={{
                                                    fontSize: '0.8rem',
                                                    backgroundColor: '#f1f5f9',
                                                    color: '#475569',
                                                    padding: '4px 12px',
                                                    borderRadius: '9999px',
                                                    fontWeight: 600,
                                                    width: '100%',
                                                    display: 'block',
                                                    textAlign: 'left'
                                                }}>
                                                    {curso.nivelNombre || "No especificado"}
                                                </div>

                                                {/* Badge de Libre Inscripción */}
                                                <div style={{
                                                    fontSize: '0.75rem',
                                                    backgroundColor: curso.autoAsignable ? '#ecfdf5' : '#fff7ed',
                                                    color: curso.autoAsignable ? '#059669' : '#c2410c',
                                                    padding: '2px 10px',
                                                    borderRadius: '9999px',
                                                    fontWeight: 600,
                                                    width: 'fit-content',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    border: `1px solid ${curso.autoAsignable ? '#10b981' : '#fdba74'}`,
                                                    marginTop: '2px'
                                                }}>
                                                    {curso.autoAsignable ? '✓ Libre inscripción' : '🔒 Requiere asignación'}
                                                </div>
                                            </div>

                                            {/* Info de Instructor y Duración (Texto simple) */}
                                            <div style={{ fontSize: '0.85rem', color: '#64748b', display: 'flex', flexDirection: 'column', gap: '0.3rem', marginTop: '0.5rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <FiClock style={{ marginRight: '6px' }} />
                                                    {curso.duracion ? `${curso.duracion} días` : "Duración no espec."}
                                                </div>

                                                {curso.instructorNombre && (
                                                    <div style={{ marginTop: '0.1rem' }}>
                                                        Instructor: {curso.instructorNombre}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="course-actions">
                                            {(!curso.autoAsignable && !(isAdminLMS && vistaAdmin === 'revisar')) ? (
                                                <button
                                                    type="button"
                                                    className="btn-action"
                                                    disabled
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.5rem 1rem',
                                                        cursor: 'pointer',
                                                        transition: '0.2s',
                                                        fontFamily: "'Outfit', sans-serif"
                                                    }}
                                                >
                                                    Requiere Asignación
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn-action"
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.5rem 1rem',
                                                        cursor: 'pointer',
                                                        transition: '0.2s',
                                                        fontFamily: "'Outfit', sans-serif"
                                                    }}
                                                    onClick={() => {
                                                        const isReviewMode = isAdminLMS && vistaAdmin === 'revisar';
                                                        if (isReviewMode) {
                                                            navigate(`/menu/lms/curso-curriculo-revision/${curso.idCurso}`);
                                                        } else {
                                                            handleRegister(curso);
                                                        }
                                                    }}
                                                    disabled={registeringId === (curso.idCurso || curso.id)}
                                                >
                                                    {(isAdminLMS && vistaAdmin === 'revisar')
                                                        ? "Revisar"
                                                        : (registeringId === (curso.idCurso || curso.id)
                                                            ? "Registrando..."
                                                            : "Registrarte")
                                                    }
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            );
                        });
                    })()}
                </div>
            )}

            {/* Modales */}
            <AlertDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
            />

            <ConfirmDialog
                open={confirmDialog.open}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
            />
        </div>
    );
};

export default CoursesMarketplace;
